import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Avatar,
    Typography,
    Button,
    TextField,
    Card,
    CardContent,
    InputAdornment,
    Paper,
    Chip,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Popover,
    Badge,
    Divider,
    Autocomplete,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert
} from '@mui/material';
import {
    Search,
    Person,
    RestartAlt,
    Close,
    Groups,
    LocationCity,
    Home,
    HowToVote,
    Tune,
    Download,
    Report,
    AddPhotoAlternate,
    Print,
    Delete,
    Edit
} from '@mui/icons-material';
import { Checkbox, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import api from '../../api/axios';
import CitizenDetailsDialog from '../../components/admin/CitizenDetailsDialog';
import AssignSubLeaderDialog from '../../components/admin/AssignSubLeaderDialog';
import VillageAutocomplete from '../../components/common/VillageAutocomplete';
import { useAuth } from '../../contexts/AuthContext';

// --- Interfaces ---
import { Citizen } from '../../types/citizen';

interface FilterState {
    gender: string;
    min_age: string;
    max_age: string;
    community: string;
    profession: string;
    blood_group: string;
    is_active_social: boolean | null;
    is_nri: boolean | null;
    is_resident_satys: boolean | null;
}

interface LocationOption {
    id: number;
    name: string;
}

const AllCitizens = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const queryClient = useQueryClient();
    // State
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);

    // Location Filter State - Multi-select arrays
    const [selectedConstituencies, setSelectedConstituencies] = useState<LocationOption[]>([]);
    const [selectedVillages, setSelectedVillages] = useState<LocationOption[]>([]);
    const [selectedBooths, setSelectedBooths] = useState<LocationOption[]>([]);
    const [selectedSubLeader, setSelectedSubLeader] = useState<any>(null);

    // Advanced Filter State (in popover)
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [filters, setFilters] = useState<FilterState>({
        gender: '',
        min_age: '',
        max_age: '',
        community: '',
        profession: '',
        blood_group: '',
        is_active_social: null,
        is_nri: null,
        is_resident_satys: null
    });

    // Export state
    const [isExporting, setIsExporting] = useState(false);

    // Complaint Dialog State
    const [complaintDialog, setComplaintDialog] = useState<{ open: boolean; citizen: Citizen | null }>({
        open: false,
        citizen: null,
    });
    // View Citizen Dialog State
    const [viewDialog, setViewDialog] = useState<{ open: boolean; citizen: Citizen | null }>({
        open: false,
        citizen: null,
    });
    // Assign Sub Leader Dialog State
    const [assignDialog, setAssignDialog] = useState<{ open: boolean; citizen: Citizen | null }>({
        open: false,
        citizen: null,
    });
    // Edit Citizen Dialog State
    const [editDialog, setEditDialog] = useState(false);
    const [editingCitizen, setEditingCitizen] = useState<Citizen | null>(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        voter_id: '',
        gender: 'male',
        age: '',
        phone: '',
        adhaar_number: '',
        community: '',
        whatsapp_number: '',
        profession: '',
        income: '',
        pro_party: '',
        dob: '',
        blood_group: '',
        is_active_social: false,
        is_nri: false,
        is_resident_satys: false,
        address: ''
    });
    const [editFormErrors, setEditFormErrors] = useState<Record<string, string>>({});
    const [complaintForm, setComplaintForm] = useState({
        description: '',
        complaint_type_id: '',
        complaint_department_id: '',
        priority: 'medium',
    });
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const [selectedImages, setSelectedImages] = useState<File[]>([]);

    // Fetch Complaint Types
    const { data: complaintTypesData } = useQuery({
        queryKey: ['complaint-types-v2'],
        queryFn: async () => {
            const res = await api.get('/tenant/complaint-types', { params: { per_page: 100 } });
            const data = res.data?.data;
            if (Array.isArray(data)) return data;
            if (data?.data && Array.isArray(data.data)) return data.data;
            return [];
        },
    });

    // Fetch Complaint Departments
    const { data: complaintDepartmentsData } = useQuery({
        queryKey: ['complaint-departments-v2'],
        queryFn: async () => {
            const res = await api.get('/tenant/complaint-departments', { params: { per_page: 100 } });
            const data = res.data?.data;
            if (Array.isArray(data)) return data;
            if (data?.data && Array.isArray(data.data)) return data.data;
            return [];
        },
    });

    // Fetch Community Options
    const { data: communityOptions = [] } = useQuery({
        queryKey: ['options-communities'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/communities');
            return res.data?.data || [];
        },
    });

    // Fetch Profession Options
    const { data: professionOptions = [] } = useQuery({
        queryKey: ['options-professions'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/professions');
            return res.data?.data || [];
        },
    });

    // Fetch Political Parties Options
    const { data: politicalPartiesOptions = [] } = useQuery({
        queryKey: ['options-political-parties'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/political-parties');
            return res.data?.data || [];
        },
    });

    // Update Citizen Mutation
    const updateCitizenMutation = useMutation({
        mutationFn: async (data: any) => api.put(`/tenant/citizens/${data.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['all-citizens'] });
            handleCloseEditDialog();
            setSnackbar({ open: true, message: 'Citizen updated successfully', severity: 'success' });
        },
        onError: (error: any) => {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to update citizen', severity: 'error' });
        },
    });

    // Create Complaint Mutation
    const createComplaintMutation = useMutation({
        mutationFn: async (formData: FormData) => {
            return api.post('/tenant/complaints', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            setSnackbar({ open: true, message: 'Complaint created successfully', severity: 'success' });
            setComplaintDialog({ open: false, citizen: null });
            setComplaintForm({ description: '', complaint_type_id: '', complaint_department_id: '', priority: 'medium' });
            setSelectedImages([]);
        },
        onError: (error: any) => {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create complaint', severity: 'error' });
        },
    });

    const handleOpenComplaintDialog = (citizen: Citizen) => {
        setComplaintDialog({ open: true, citizen });
        setComplaintForm({ description: '', complaint_type_id: '', complaint_department_id: '', priority: 'medium' });
        setSelectedImages([]);
    };

    const handleCloseComplaintDialog = () => {
        setComplaintDialog({ open: false, citizen: null });
        setComplaintForm({
            description: '',
            complaint_type_id: '',
            complaint_department_id: '',
            priority: 'medium',
        });
        setSelectedImages([]);
    };

    const handleOpenViewDialog = (citizen: Citizen) => {
        setViewDialog({ open: true, citizen });
    };

    const handleCloseViewDialog = () => {
        setViewDialog({ open: false, citizen: null });
    };

    const handleOpenAssignDialog = (citizen: Citizen) => {
        setAssignDialog({ open: true, citizen });
    };

    const handleCloseAssignDialog = () => {
        setAssignDialog({ open: false, citizen: null });
    };

    const handleOpenEditDialog = (citizen: Citizen) => {
        setEditingCitizen(citizen);
        setEditFormData({
            name: citizen.name || '',
            voter_id: citizen.voter_id || '',
            gender: (citizen.gender?.toLowerCase() || 'male') as any,
            age: citizen.age ? String(citizen.age) : '',
            phone: citizen.phone || (citizen as any).mobile_number || '',
            adhaar_number: citizen.adhaar_number || '',
            community: citizen.community || '',
            whatsapp_number: citizen.whatsapp_number || '',
            profession: citizen.profession || '',
            income: citizen.income || '',
            pro_party: citizen.pro_party || '',
            dob: citizen.dob || '',
            blood_group: citizen.blood_group || '',
            is_active_social: Boolean(citizen.is_active_social),
            is_nri: Boolean(citizen.is_nri),
            is_resident_satys: Boolean(citizen.is_resident_satys),
            address: citizen.address || ''
        });
        setEditFormErrors({});
        setEditDialog(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialog(false);
        setEditingCitizen(null);
        setEditFormErrors({});
    };

    const handleEditSubmit = () => {
        const errors: Record<string, string> = {};
        if (!editFormData.name) errors.name = 'Name is required';
        if (!editFormData.gender) errors.gender = 'Gender is required';
        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }
        if (!editingCitizen) return;
        updateCitizenMutation.mutate({
            ...editFormData,
            id: editingCitizen.id,
            age: editFormData.age ? Number(editFormData.age) : null,
            is_active_social: editFormData.is_active_social ? 1 : 0,
            is_nri: editFormData.is_nri ? 1 : 0,
            is_resident_satys: editFormData.is_resident_satys ? 1 : 0,
        });
    };

    const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const files = Array.from(event.target.files);
            const maxSize = 250 * 1024; // 250KB
            const validFiles = files.filter(file => {
                if (file.size > maxSize) {
                    setSnackbar({ open: true, message: `Image "${file.name}" exceeds 250KB limit`, severity: 'error' });
                    return false;
                }
                return true;
            });
            setSelectedImages(prev => [...prev, ...validFiles]);
        }
    };

    const handleRemoveImage = (index: number) => {
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleCreateComplaint = () => {
        if (!complaintDialog.citizen) return;

        const formData = new FormData();
        formData.append('description', complaintForm.description);
        formData.append('complaint_type', 'individual');
        formData.append('complaint_type_id', complaintForm.complaint_type_id);
        formData.append('complaint_department_id', complaintForm.complaint_department_id);
        formData.append('constituency_id', String(complaintDialog.citizen.constituency_id));
        formData.append('priority', complaintForm.priority);
        formData.append('individual_citizen_id', String(complaintDialog.citizen.id));

        // Append images
        selectedImages.forEach((image) => {
            formData.append('images[]', image);
        });

        createComplaintMutation.mutate(formData);
    };

    // Reset page when filters change
    useEffect(() => {
        setPageModel(prev => ({ ...prev, page: 0 }));
    }, [selectedConstituencies, selectedVillages, selectedBooths, selectedSubLeader, filters, debouncedSearch]);

    // Fetch Constituencies
    const { data: constituenciesData, isLoading: constituenciesLoading } = useQuery({
        queryKey: ['constituencies-list'],
        queryFn: async () => {
            const res = await api.get('/tenant/constituencies', { params: { per_page: 100 } });
            return res.data?.data?.data || res.data?.data || [];
        }
    });

    // Fetch Voting Booths (for all selected villages, or all if none selected)
    const { data: boothsData, isLoading: boothsLoading } = useQuery({
        queryKey: ['booths-list', selectedVillages.map(v => v.id)],
        queryFn: async () => {
            const params: any = { per_page: 500 };
            if (selectedVillages.length > 0) {
                params['village_id[]'] = selectedVillages.map(v => v.id);
            }
            const res = await api.get('/tenant/voting-booths', { params });
            return res.data?.data?.data || res.data?.data || [];
        }
    });

    // Count active advanced filters
    const activeFilterCount = Object.values(filters).filter(val => val !== '' && val !== null && val !== false).length;

    // Fetch Sub Leaders
    const { data: subLeadersData, isLoading: subLeadersLoading } = useQuery({
        queryKey: ['sub-leaders-list'],
        queryFn: async () => {
            const res = await api.get('/tenant/sub-leaders', { params: { per_page: 500 } });
            return res.data?.data?.data || res.data?.data || [];
        }
    });

    // Count active location filters
    const locationFilterCount = selectedConstituencies.length + selectedVillages.length + selectedBooths.length + (selectedSubLeader ? 1 : 0);

    // Fetch Citizens
    const { data: citizensData, isLoading: citizensLoading } = useQuery({
        queryKey: ['all-citizens', pageModel.page, pageModel.pageSize, debouncedSearch, filters,
            selectedConstituencies.map(c => c.id), selectedVillages.map(v => v.id), selectedBooths.map(b => b.id), selectedSubLeader?.id],
        queryFn: async () => {
            const params: any = {
                page: pageModel.page + 1,
                per_page: pageModel.pageSize,
                ...filters
            };

            // Add search
            if (debouncedSearch) params.search = debouncedSearch;

            // Add multi-select location filters
            if (selectedConstituencies.length > 0) {
                params['constituency_id[]'] = selectedConstituencies.map(c => c.id);
            }
            if (selectedVillages.length > 0) {
                params['village_id[]'] = selectedVillages.map(v => v.id);
            }
            if (selectedBooths.length > 0) {
                params['voting_booth_id[]'] = selectedBooths.map(b => b.id);
            }
            if (selectedSubLeader) {
                params.sub_leader_id = selectedSubLeader.id;
            }

            // Clean up null/empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const res = await api.get('/tenant/citizens', { params });
            return res.data;
        },
        placeholderData: keepPreviousData,
    });

    // Filter Handlers
    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleFilterChange = (field: keyof FilterState, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            gender: '',
            min_age: '',
            max_age: '',
            community: '',
            profession: '',
            blood_group: '',
            is_active_social: null,
            is_nri: null,
            is_resident_satys: null
        });
    };

    const handleResetLocationFilters = () => {
        setSelectedConstituencies([]);
        setSelectedVillages([]);
        setSelectedBooths([]);
        setSelectedSubLeader(null);
    };

    const handleResetAllFilters = () => {
        handleResetFilters();
        handleResetLocationFilters();
        setSearch('');
    };

    // Export to CSV
    const handleExportCSV = async () => {
        setIsExporting(true);
        try {
            const params: any = { ...filters };

            // Add search
            if (debouncedSearch) params.search = debouncedSearch;

            // Add multi-select location filters
            if (selectedConstituencies.length > 0) {
                params['constituency_id[]'] = selectedConstituencies.map(c => c.id);
            }
            if (selectedVillages.length > 0) {
                params['village_id[]'] = selectedVillages.map(v => v.id);
            }
            if (selectedBooths.length > 0) {
                params['voting_booth_id[]'] = selectedBooths.map(b => b.id);
            }
            if (selectedSubLeader) {
                params.sub_leader_id = selectedSubLeader.id;
            }

            // Clean up null/empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await api.get('/tenant/citizens/export', {
                params,
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `citizens_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
            alert('Failed to export citizens. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // Helper for professional consistent colors (Teal, Indigo, Blue, Violet, Slate)
    const stringToColor = (string: string) => {
        const colors = [
            ['#0f766e', '#ccfbf1'], // Teal
            ['#4338ca', '#e0e7ff'], // Indigo
            ['#1d4ed8', '#dbeafe'], // Blue
            ['#7c3aed', '#ede9fe'], // Violet
            ['#be185d', '#fce7f3'], // Pink
            ['#b45309', '#fef3c7'], // Amber
        ];
        let hash = 0;
        for (let i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    // Columns
    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: '#',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7, fontFamily: 'Inter, sans-serif' }}>
                    {(pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1}
                </Typography>
            ),
        },
        {
            field: 'voter_id',
            headerName: 'Voter ID',
            width: 140,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: 1.5,
                        fontWeight: 600,
                        borderColor: 'divider',
                        bgcolor: 'rgba(0,0,0,0.01)',
                        color: 'text.primary',
                        fontFamily: 'monospace'
                    }}
                />
            )
        },
        {
            field: 'name',
            headerName: 'Citizen',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                const [darkColor, lightColor] = stringToColor(params.value);
                return (
                    <Box
                        onClick={() => handleOpenViewDialog(params.row)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            cursor: 'pointer',
                            p: 0.5,
                            borderRadius: 2,
                            width: '100%',
                            transition: 'background-color 0.2s',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: lightColor,
                                color: darkColor,
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                border: `1px solid ${darkColor}20`
                            }}
                        >
                            {params.value.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', letterSpacing: '-0.01em', textDecoration: 'underline', textDecorationColor: 'transparent', transition: 'text-decoration-color 0.2s', '&:hover': { textDecorationColor: 'inherit' } }}>
                            {params.value}
                        </Typography>
                    </Box>
                );
            }
        },
        { field: 'phone', headerName: 'Phone Number', width: 140, renderCell: (params) => params.value ? <Typography variant="body2" fontFamily="monospace" color="text.secondary">{params.value}</Typography> : '-' },
        { field: 'adhaar_number', headerName: 'Aadhaar Number', width: 150, renderCell: (params) => params.value ? <Typography variant="body2" fontFamily="monospace" letterSpacing={1}>{params.value}</Typography> : '-' },
        { field: 'age', headerName: 'Age', width: 70 },
        {
            field: 'gender',
            headerName: 'Gender',
            width: 100,
            renderCell: (params) => {
                const isMale = params.value === 'male';
                return params.value ? (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={{
                            textTransform: 'capitalize',
                            borderRadius: '6px',
                            fontWeight: 500,
                            bgcolor: isMale ? '#eff6ff' : '#fdf2f8',
                            color: isMale ? '#1d4ed8' : '#db2777'
                        }}
                    />
                ) : '-';
            }
        },
        { field: 'whatsapp_number', headerName: 'WhatsApp', width: 130, renderCell: (params) => params.value ? <Typography variant="caption" fontFamily="monospace" color="text.secondary">{params.value}</Typography> : <Typography color="text.disabled">-</Typography> },
        { field: 'community', headerName: 'Community', width: 120 },
        { field: 'profession', headerName: 'Profession', width: 120 },
        {
            field: 'is_active_social',
            headerName: 'Status',
            width: 110,
            renderCell: (params) => params.value ?
                <Chip label="Active" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 600, borderRadius: '6px' }} /> :
                <Chip label="Inactive" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', borderRadius: '6px' }} />
        },
        {
            field: 'is_nri',
            headerName: 'NRI',
            renderCell: (params) => params.value ? <Chip label="NRI" size="small" sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 600, borderRadius: '6px' }} /> : null
        },
        {
            field: 'actions',
            headerName: 'Action',
            width: 220,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {hasPermission('citizens.update') && (
                        <Tooltip title="Edit Citizen">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(params.row); }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {hasPermission('complaints.create') && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Report fontSize="small" />}
                            onClick={(e) => { e.stopPropagation(); handleOpenComplaintDialog(params.row); }}
                            sx={{
                                bgcolor: '#f59e0b',
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                padding: '4px 12px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)',
                                '&:hover': {
                                    bgcolor: '#d97706',
                                    boxShadow: '0 4px 6px rgba(245, 158, 11, 0.3)',
                                }
                            }}
                        >
                            Citizen Request
                        </Button>
                    )}
                    <Tooltip title="Assign Sub Leader">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleOpenAssignDialog(params.row); }}
                        >
                            <Person fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    // Safe total count access
    const totalCitizens = citizensData?.data?.total || citizensData?.meta?.total || 0;
    const citizensList = citizensData?.data?.data || citizensData?.data || [];

    const totalActiveFilters = activeFilterCount + locationFilterCount + (search ? 1 : 0);

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Groups fontSize="large" />
                        All Citizens
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
                        View and filter all citizens across constituencies, villages, and voting booths.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                    <Chip
                        label={`${totalCitizens.toLocaleString()} Citizens`}
                        color="default"
                        variant="outlined"
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', fontWeight: 'bold' }}
                    />
                    {totalActiveFilters > 0 && (
                        <Chip
                            label={`${totalActiveFilters} Filters Active`}
                            color="warning"
                            size="small"
                            onDelete={handleResetAllFilters}
                            sx={{ color: 'white' }}
                        />
                    )}
                </Stack>
            </Paper>

            {/* Location Filters - Multi-Select */}
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationCity fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                Location Filters
                            </Typography>
                            <Chip label="Multi-select" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                        {locationFilterCount > 0 && (
                            <Button
                                size="small"
                                startIcon={<RestartAlt />}
                                onClick={handleResetLocationFilters}
                                sx={{ color: 'text.secondary', textTransform: 'none' }}
                            >
                                Clear ({locationFilterCount})
                            </Button>
                        )}
                    </Box>
                    <Grid container spacing={2} sx={{ p: 3 }}>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={constituenciesData || []}
                                getOptionLabel={(option: LocationOption) => option.name}
                                value={selectedConstituencies}
                                onChange={(_, newValue) => setSelectedConstituencies(newValue)}
                                loading={constituenciesLoading}
                                limitTags={2}
                                disableCloseOnSelect
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            size="small"
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.name}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Constituencies"
                                        placeholder={selectedConstituencies.length === 0 ? "All" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <LocationCity fontSize="small" color="action" />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {constituenciesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <VillageAutocomplete
                                multiple
                                value={selectedVillages}
                                onChange={(newValue: any) => setSelectedVillages(newValue)}
                                constituencyIds={selectedConstituencies.map(c => c.id)}
                                limitTags={2}
                                label="Villages"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={boothsData || []}
                                getOptionLabel={(option: LocationOption) => option.name}
                                value={selectedBooths}
                                onChange={(_, newValue) => setSelectedBooths(newValue)}
                                loading={boothsLoading}
                                limitTags={2}
                                disableCloseOnSelect
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            size="small"
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.name}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Voting Booths"
                                        placeholder={selectedBooths.length === 0 ? "All" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <HowToVote fontSize="small" color="action" />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {boothsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <Autocomplete
                                size="small"
                                options={subLeadersData || []}
                                getOptionLabel={(option: any) => `${option.first_name || ''} ${option.last_name || ''}`.trim()}
                                value={selectedSubLeader}
                                onChange={(_, newValue) => setSelectedSubLeader(newValue)}
                                loading={subLeadersLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Sub Leader"
                                        placeholder={selectedSubLeader ? '' : 'All'}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <Person fontSize="small" color="action" />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {subLeadersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Actions & Search Bar */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Search citizens (Name, Voter ID, Mobile)..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        flexGrow: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                        }
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment>,
                    }}
                />

                <Stack direction="row" spacing={1.5}>
                    <Badge badgeContent={activeFilterCount} color="error">
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<Tune />}
                            onClick={handleFilterClick}
                            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: 'background.paper', borderColor: 'divider' }}
                        >
                            Filters
                        </Button>
                    </Badge>
                    {hasPermission('reports.export') && (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <Download />}
                            onClick={handleExportCSV}
                            disabled={isExporting || citizensLoading}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                            {isExporting ? 'Exporting...' : 'Export CSV'}
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Advanced Filter Popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: { width: 400, p: 2 }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">Advanced Filters</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            startIcon={<RestartAlt />}
                            onClick={handleResetFilters}
                            disabled={activeFilterCount === 0}
                        >
                            Reset
                        </Button>
                        <IconButton size="small" onClick={handleFilterClose}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Gender</InputLabel>
                            <Select
                                value={filters.gender}
                                label="Gender"
                                onChange={(e) => handleFilterChange('gender', e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Blood Group</InputLabel>
                            <Select
                                value={filters.blood_group}
                                label="Blood Group"
                                onChange={(e) => handleFilterChange('blood_group', e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="A+">A+</MenuItem>
                                <MenuItem value="A-">A-</MenuItem>
                                <MenuItem value="B+">B+</MenuItem>
                                <MenuItem value="B-">B-</MenuItem>
                                <MenuItem value="AB+">AB+</MenuItem>
                                <MenuItem value="AB-">AB-</MenuItem>
                                <MenuItem value="O+">O+</MenuItem>
                                <MenuItem value="O-">O-</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Min Age"
                            type="number"
                            size="small"
                            fullWidth
                            value={filters.min_age}
                            onChange={(e) => handleFilterChange('min_age', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Max Age"
                            type="number"
                            size="small"
                            fullWidth
                            value={filters.max_age}
                            onChange={(e) => handleFilterChange('max_age', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Autocomplete
                            size="small"
                            options={communityOptions.map((c: any) => c.name)}
                            value={filters.community || null}
                            onChange={(_, newValue) => handleFilterChange('community', newValue || '')}
                            renderInput={(params) => (
                                <TextField {...params} label="Community" fullWidth />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Autocomplete
                            size="small"
                            options={professionOptions.map((p: any) => p.name)}
                            value={filters.profession || null}
                            onChange={(_, newValue) => handleFilterChange('profession', newValue || '')}
                            renderInput={(params) => (
                                <TextField {...params} label="Profession" fullWidth />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.is_active_social === true}
                                    indeterminate={filters.is_active_social === null}
                                    onChange={() => {
                                        if (filters.is_active_social === null) handleFilterChange('is_active_social', true);
                                        else if (filters.is_active_social === true) handleFilterChange('is_active_social', false);
                                        else handleFilterChange('is_active_social', null);
                                    }}
                                />
                            }
                            label="Active Socially"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.is_nri === true}
                                    indeterminate={filters.is_nri === null}
                                    onChange={() => {
                                        if (filters.is_nri === null) handleFilterChange('is_nri', true);
                                        else if (filters.is_nri === true) handleFilterChange('is_nri', false);
                                        else handleFilterChange('is_nri', null);
                                    }}
                                />
                            }
                            label="NRI"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.is_resident_satys === true}
                                    indeterminate={filters.is_resident_satys === null}
                                    onChange={() => {
                                        if (filters.is_resident_satys === null) handleFilterChange('is_resident_satys', true);
                                        else if (filters.is_resident_satys === true) handleFilterChange('is_resident_satys', false);
                                        else handleFilterChange('is_resident_satys', null);
                                    }}
                                />
                            }
                            label="Resident Stays"
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Button fullWidth variant="contained" onClick={handleFilterClose}>
                            Apply Filters
                        </Button>
                    </Grid>
                </Grid>
            </Popover>

            {/* Data Table */}
            <Card sx={{
                borderRadius: 4,
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.03)'
            }}>
                <DataGrid
                    rows={citizensList}
                    columns={columns}
                    loading={citizensLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    rowCount={totalCitizens}
                    autoHeight
                    disableRowSelectionOnClick
                    density="comfortable"
                    rowHeight={64} // Balanced height
                    sx={{
                        border: 0,
                        backgroundColor: 'white',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: 'white',
                            color: '#475569', // Slate-600
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            borderBottom: '1px solid #e2e8f0',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                            color: '#334155', // Slate-700
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc', // Very subtle slate-50
                            cursor: 'pointer',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '1px solid #e2e8f0',
                        }
                    }}
                />
            </Card>

            {/* Complaint Dialog */}
            <Dialog open={complaintDialog.open} onClose={handleCloseComplaintDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2
                }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}>
                        <Report />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">New Citizen Request</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>Start a new citizen request case</Typography>
                    </Box>
                    <IconButton onClick={handleCloseComplaintDialog} sx={{ ml: 'auto', color: 'white' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, bgcolor: '#f8fafc' }}>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        {complaintDialog.citizen && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 3,
                                    bgcolor: 'white'
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                    <Avatar
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: stringToColor(complaintDialog.citizen.name)[1],
                                            color: stringToColor(complaintDialog.citizen.name)[0],
                                            fontWeight: 'bold',
                                            border: '1px solid rgba(0,0,0,0.05)'
                                        }}
                                    >
                                        {complaintDialog.citizen.name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">
                                            {complaintDialog.citizen.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, mb: 1 }}>
                                            <Chip
                                                label={`VID: ${complaintDialog.citizen.voter_id}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ borderRadius: 1.5, fontSize: '0.7rem', height: 20 }}
                                            />
                                            {complaintDialog.citizen.phone && (
                                                <Chip
                                                    label={complaintDialog.citizen.phone}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1.5, fontSize: '0.7rem', height: 20 }}
                                                />
                                            )}
                                            {complaintDialog.citizen.community && (
                                                <Chip
                                                    label={complaintDialog.citizen.community}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1.5, fontSize: '0.7rem', height: 20 }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.4 }}>
                                            {complaintDialog.citizen.constituency?.name} • {complaintDialog.citizen.village?.name}
                                            {complaintDialog.citizen.voting_booth && ` • ${complaintDialog.citizen.voting_booth.name} (#${complaintDialog.citizen.voting_booth.booth_id})`}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        <FormControl fullWidth required>
                            <InputLabel>Request Type</InputLabel>
                            <Select
                                value={complaintForm.complaint_type_id}
                                label="Request Type"
                                onChange={(e) => setComplaintForm({ ...complaintForm, complaint_type_id: e.target.value })}
                            >
                                {(Array.isArray(complaintTypesData) ? complaintTypesData : []).map((type: any) => (
                                    <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Description"
                            value={complaintForm.description}
                            onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
                            multiline
                            rows={3}
                            required
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Department</InputLabel>
                            <Select
                                value={complaintForm.complaint_department_id}
                                label="Department"
                                onChange={(e) => setComplaintForm({ ...complaintForm, complaint_department_id: e.target.value })}
                            >
                                {(Array.isArray(complaintDepartmentsData) ? complaintDepartmentsData : []).map((dept: any) => (
                                    <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth required>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={complaintForm.priority}
                                label="Priority"
                                onChange={(e) => setComplaintForm({ ...complaintForm, priority: e.target.value })}
                            >
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Image Upload Section */}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Attach Images (Optional)
                            </Typography>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageSelect}
                                style={{ display: 'none' }}
                                id="complaint-image-input"
                            />
                            <label htmlFor="complaint-image-input">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<AddPhotoAlternate />}
                                    size="small"
                                >
                                    Add Images
                                </Button>
                            </label>
                            {selectedImages.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                    {selectedImages.map((file, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                position: 'relative',
                                                width: 80,
                                                height: 80,
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={`Preview ${index + 1}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: 'white',
                                                    '&:hover': { bgcolor: 'error.main' },
                                                }}
                                                onClick={() => handleRemoveImage(index)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseComplaintDialog} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleCreateComplaint}
                        variant="contained"
                        color="warning"
                        disabled={createComplaintMutation.isPending || !complaintForm.description || !complaintForm.complaint_type_id || !complaintForm.complaint_department_id}
                        startIcon={createComplaintMutation.isPending ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {createComplaintMutation.isPending ? 'Submitting...' : 'Create Citizen Request'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Citizen Dialog */}
            <Dialog open={editDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
                <DialogTitle>Edit Citizen</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Name" fullWidth required value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} error={!!editFormErrors.name} helperText={editFormErrors.name} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Voter ID" fullWidth value={editFormData.voter_id} onChange={(e) => setEditFormData({ ...editFormData, voter_id: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!!editFormErrors.gender}>
                                <InputLabel>Gender</InputLabel>
                                <Select value={editFormData.gender} label="Gender" onChange={(e) => setEditFormData({ ...editFormData, gender: e.target.value as any })}>
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Age" type="number" fullWidth value={editFormData.age} onChange={(e) => setEditFormData({ ...editFormData, age: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Mobile Number" fullWidth value={editFormData.phone} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Aadhaar Number" fullWidth value={editFormData.adhaar_number} onChange={(e) => setEditFormData({ ...editFormData, adhaar_number: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete freeSolo options={communityOptions?.map((c: any) => c.name) || []} value={editFormData.community} onChange={(_e, v) => setEditFormData({ ...editFormData, community: v || '' })} onInputChange={(_e, v) => setEditFormData({ ...editFormData, community: v })} renderInput={(params) => <TextField {...params} label="Community" fullWidth />} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="WhatsApp Number" fullWidth value={editFormData.whatsapp_number} onChange={(e) => setEditFormData({ ...editFormData, whatsapp_number: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete freeSolo options={professionOptions?.map((p: any) => p.name) || []} value={editFormData.profession} onChange={(_e, v) => setEditFormData({ ...editFormData, profession: v || '' })} onInputChange={(_e, v) => setEditFormData({ ...editFormData, profession: v })} renderInput={(params) => <TextField {...params} label="Profession" fullWidth />} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Income" fullWidth value={editFormData.income} onChange={(e) => setEditFormData({ ...editFormData, income: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete freeSolo options={politicalPartiesOptions?.map((p: any) => p.name) || []} value={editFormData.pro_party} onChange={(_e, v) => setEditFormData({ ...editFormData, pro_party: v || '' })} onInputChange={(_e, v) => setEditFormData({ ...editFormData, pro_party: v })} renderInput={(params) => <TextField {...params} label="Political Party" fullWidth />} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Blood Group</InputLabel>
                                <Select value={editFormData.blood_group} label="Blood Group" onChange={(e) => setEditFormData({ ...editFormData, blood_group: e.target.value })}>
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="A+">A+</MenuItem><MenuItem value="A-">A-</MenuItem>
                                    <MenuItem value="B+">B+</MenuItem><MenuItem value="B-">B-</MenuItem>
                                    <MenuItem value="AB+">AB+</MenuItem><MenuItem value="AB-">AB-</MenuItem>
                                    <MenuItem value="O+">O+</MenuItem><MenuItem value="O-">O-</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField label="Date of Birth" type="date" fullWidth InputLabelProps={{ shrink: true }} value={editFormData.dob} onChange={(e) => setEditFormData({ ...editFormData, dob: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControlLabel control={<Checkbox checked={editFormData.is_active_social} onChange={(e) => setEditFormData({ ...editFormData, is_active_social: e.target.checked })} />} label="Active Socially" />
                                <FormControlLabel control={<Checkbox checked={editFormData.is_nri} onChange={(e) => setEditFormData({ ...editFormData, is_nri: e.target.checked })} />} label="NRI" />
                                <FormControlLabel control={<Checkbox checked={editFormData.is_resident_satys} onChange={(e) => setEditFormData({ ...editFormData, is_resident_satys: e.target.checked })} />} label="Resident Stays" />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField label="Address" fullWidth multiline rows={2} value={editFormData.address} onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseEditDialog}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant="contained" disabled={updateCitizenMutation.isPending}>
                        {updateCitizenMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Citizen Dialog */}
            <CitizenDetailsDialog
                open={viewDialog.open}
                onClose={handleCloseViewDialog}
                citizen={viewDialog.citizen}
                onEdit={(citizen) => { handleCloseViewDialog(); handleOpenEditDialog(citizen); }}
            />

            <AssignSubLeaderDialog
                open={assignDialog.open}
                onClose={handleCloseAssignDialog}
                citizen={assignDialog.citizen}
                onSuccess={() => {
                    setSnackbar({ open: true, message: 'Sub leader assigned successfully', severity: 'success' });
                }}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box >
    );
};

export default AllCitizens;
